import { UIEvent, useCallback, useEffect, useState } from 'react';

import { embla } from 'ui/utilities';
import { clickEvents } from 'ui/utilities/helpers/click-events';

import type { CarouselState, SelectableState } from './Carousel.d';

export const useCarousel = ({
  autoplay,
  container,
  desktop,
  duration,
  infinite,
  itemsPerPage,
  navigationSize,
  selectable,
  selectedItemIndex,
  gaEvents,
}: Omit<CarouselState, 'pageList' | 'page' | 'isNextVisible' | 'isPrevVisible'> &
  SelectableState) => {
  const [carouselState, setCarouselState] = useState<CarouselState>({
    desktop: false,
    dots: true,
    duration: 10_000,
    infinite: false,
    isNextVisible: false,
    isPrevVisible: false,
    itemsPerPage: 1,
    pageList: [],
    page: 0,
  });
  const [selectedState, setSelectedState] = useState<SelectableState>({
    selectable,
    selectedItemIndex,
  });
  const {
    useEmblaCarousel,
    plugins: { Autoplay },
  } = embla;

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: 'start',
      loop: infinite,
      slidesToScroll: 'auto',
      containScroll: 'trimSnaps',
    },
    [Autoplay({ delay: duration, playOnInit: autoplay })],
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
    }
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
    }
  }, [emblaApi]);

  const scrollTo = useCallback(
    (event: UIEvent<HTMLButtonElement>) => {
      const { index = 0 } = event.currentTarget.dataset;

      if (!emblaApi) {
        return;
      }

      emblaApi.scrollTo(Number(index));
      gaEvents &&
        clickEvents(
          `click:carroussel:${Number(index) + 1}`,
          'interaction_catalogos',
          'ciclo-atual',
        );
    },
    [emblaApi],
  );

  const toggleSelected = (event: UIEvent) => {
    const { dataset } = event.currentTarget as HTMLElement;
    const { index } = dataset;

    if (selectedState.selectedItemIndex === Number(index) || index === undefined) {
      return setSelectedState({ ...selectedState, selectedItemIndex: undefined });
    }

    return setSelectedState({ ...selectedState, selectedItemIndex: Number(index) });
  };

  const updateCarouselBase = () => {
    if (emblaApi) {
      setCarouselState({
        ...carouselState,
        page: emblaApi?.selectedScrollSnap(),
        pageList: emblaApi?.scrollSnapList(),
        isPrevVisible: emblaApi?.canScrollPrev(),
        isNextVisible: emblaApi?.canScrollNext(),
      });
    }
  };

  useEffect(() => {
    return setCarouselState({
      ...carouselState,
      autoplay,
      container,
      desktop: desktop || false,
      duration: duration || 10000,
      itemsPerPage: itemsPerPage || 1,
      infinite: infinite || false,
      navigationSize,
      pageList: emblaApi?.scrollSnapList() || [],
    });
  }, [
    autoplay,
    container,
    desktop,
    duration,
    infinite,
    itemsPerPage,
    navigationSize,
    selectable,
    selectedItemIndex,
  ]);

  useEffect(() => {
    if (emblaApi) {
      updateCarouselBase();

      emblaApi.on('select', updateCarouselBase);
      emblaApi.on('reInit', updateCarouselBase);
      emblaApi.on('resize', () => {
        emblaApi.reInit({ slidesToScroll: itemsPerPage });
      });
    }
  }, [emblaApi]);

  return {
    emblaRef,
    emblaApi,
    state: {
      ...carouselState,
      ...selectedState,
    },
    scrollPrev,
    scrollNext,
    scrollTo,
    toggleSelected,
  };
};
